import {
  ALL_EUROPEAN_BRANDS_URL_ID,
  EUROPEAN_BRANDS_URL_ID,
  EXCLUSIVES_CATEGORY_URL_ID,
  NEW_BRANDS_CATEGORY_URL_ID,
  NEW_PRODUCTS_CATEGORY_URL_ID,
} from "@faire/retailer-shared/categories/urlId";
import {
  BRAND_VALUE_FILTER_TYPE,
  getFilterValueFromFilterKey,
  isFilterKeyOfType,
} from "@faire/retailer-shared/filters/filterKeys";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import isEmpty from "lodash/isEmpty";
import { useEffect, useMemo, type JSX } from "react";

import { BRAND_VALUES } from "@faire/retailer/consts/BRAND_VALUES";
import { useInsiderHelpCenterArticleLink } from "@faire/retailer/hooks/insider/useInsiderHelpCenterArticleLink";
import { Category } from "@faire/retailer/models/Category/Category";
import { getCategoryDisplayName } from "@faire/retailer/services/Categories";
import { useCategoryStore } from "@faire/retailer/stores/domain/CategoryStore";
import { findActiveCategoryByUrlIds } from "@faire/retailer/stores/domain/CategoryStore/findByUrlId";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { useRecordMilestoneOnce } from "@faire/retailer/stores/domain/useMilestone";
import { useCategoryPageFilterKeys } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/hooks/useCategoryPageFilterKeys";
import { useLayoutElementPageSubheader } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/LayoutElements/useLayoutElementPageSubheader";
import { subheaderForLoggedInUsers } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/HeaderAndSubheader/__internal__/CategoryMapping";
import {
  getBestsellersCategoryHeaderText,
  getEcoFriendlyCategoryHeaderText,
  getInsiderEarlyAccessSubheaderText,
  getNewBrandsCategorySubheaderText,
  getNewProductsCategorySubheaderText,
  getSaleCategoryHeaderText,
  getShowroomCategoryHeaderText,
} from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/HeaderAndSubheader/__internal__/titleTextUtils";
import { useIsBestsellers } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/useIsBestsellers";
import { useIsSaleHub } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/useIsSaleHub";
import { useCurrentCategoryModel } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useCurrentCategoryModel";
import { useIsHubCategory } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/useIsHubCategory";

export const useCategoryPageHeaderAndSubheaderTexts = () => {
  const { strictLocalize } = useStrictLocalization();
  const categoryModel = useCurrentCategoryModel();
  const { categories } = useCategoryStore();
  const currentCategory = findActiveCategoryByUrlIds(
    categoryModel.urlIdStrings,
    categories
  );
  const { isBestsellersProductCategory } = useIsBestsellers();
  const { isSaleProductCategory } = useIsSaleHub();
  const { isAmerican, isQualifiedForEuropeFreeShipping, shouldShowInsider } =
    useRetailerStore([
      "isAmerican",
      "isQualifiedForEuropeFreeShipping",
      "shouldShowInsider",
    ]);
  const layoutElementSubheader = useLayoutElementPageSubheader();

  const helpCenterLink = useInsiderHelpCenterArticleLink();
  const {
    isNewArrivalsSubCategory,
    isNewBrandsC1,
    isNewProductsC1,
    isNewProductsSubCategory,
    isPlusSize,
    isPrimaryCategory,
    isEcoFriendlySubCategory,
    isSubcategoryOfShowroomC1,
    mostSpecificPathIdentifier,
    mostSpecificName,
    mostSpecificUniqueDisplayName,
    urlId,
  } = categoryModel;

  const gridHeaderText = currentCategory?.category_results_header;

  const headerText = useMemo(() => {
    if (isNewProductsC1) {
      return getCategoryDisplayName(
        NEW_PRODUCTS_CATEGORY_URL_ID,
        strictLocalize
      );
    }

    if (isNewBrandsC1) {
      return getCategoryDisplayName(NEW_BRANDS_CATEGORY_URL_ID, strictLocalize);
    }

    if (isBestsellersProductCategory) {
      return getBestsellersCategoryHeaderText(mostSpecificName, strictLocalize);
    }

    if (isSaleProductCategory) {
      return getSaleCategoryHeaderText(mostSpecificName, strictLocalize);
    }

    if (gridHeaderText) {
      return undefined;
    }

    if (isPlusSize && mostSpecificUniqueDisplayName) {
      return mostSpecificUniqueDisplayName;
    }

    if (isEcoFriendlySubCategory) {
      return getEcoFriendlyCategoryHeaderText(
        mostSpecificName,
        getParentCategoryName(categoryModel)
      );
    }

    if (isSubcategoryOfShowroomC1) {
      return getShowroomCategoryHeaderText(
        mostSpecificName,
        categoryModel.name
      );
    }

    return getCategoryDisplayName(mostSpecificName, strictLocalize);
  }, [
    isNewProductsC1,
    isNewBrandsC1,
    isBestsellersProductCategory,
    isSaleProductCategory,
    isSubcategoryOfShowroomC1,
    gridHeaderText,
    isPlusSize,
    mostSpecificUniqueDisplayName,
    isEcoFriendlySubCategory,
    mostSpecificName,
    strictLocalize,
    categoryModel,
  ]);

  const showEuropeFreeShippingSubheader =
    categoryModel.urlId.id === ALL_EUROPEAN_BRANDS_URL_ID &&
    isQualifiedForEuropeFreeShipping;

  const selectedMakerValues = useSelectedMakerValues();
  const { isHubCategory, isHubC1Subcategory } = useIsHubCategory();

  const isNonAmericanInsider = !isAmerican && shouldShowInsider;

  const subheaderText = useMemo(() => {
    if (layoutElementSubheader) {
      return layoutElementSubheader;
    }

    if (isHubCategory || isHubC1Subcategory) {
      return undefined;
    }

    if (mostSpecificPathIdentifier === EXCLUSIVES_CATEGORY_URL_ID) {
      return getInsiderEarlyAccessSubheaderText(
        isNonAmericanInsider,
        helpCenterLink
      );
    }

    const showNewProductsC1Subheader = isNewProductsC1 === true;
    if (showNewProductsC1Subheader) {
      return getNewProductsCategorySubheaderText(
        mostSpecificName,
        strictLocalize
      );
    }

    const showNewBrandsC1Subheader =
      isNewBrandsC1 === true && !isPrimaryCategory;
    if (showNewBrandsC1Subheader) {
      return getNewBrandsCategorySubheaderText(
        mostSpecificName,
        strictLocalize
      );
    }

    const isEuropeanBrandsCategory = urlId.id === EUROPEAN_BRANDS_URL_ID;
    const showNewProductsSubcategorySubheader =
      isNewProductsSubCategory === true && !isEuropeanBrandsCategory;

    if (showNewProductsSubcategorySubheader) {
      const parentCategoryName =
        getParentCategoryName(categoryModel) ?? categoryModel.name;
      return getNewProductsCategorySubheaderText(
        parentCategoryName ?? "",
        strictLocalize
      );
    }

    const showNewBrandsSubcategorySubheader =
      isNewArrivalsSubCategory === true && !isEuropeanBrandsCategory;

    if (showNewBrandsSubcategorySubheader) {
      const parentCategoryName =
        getParentCategoryName(categoryModel) ?? categoryModel.name;
      return getNewBrandsCategorySubheaderText(
        parentCategoryName,
        strictLocalize
      );
    }

    if (categoryModel.isAllBrands && !isEmpty(selectedMakerValues)) {
      const readableMakerValues = selectedMakerValues
        .filter((value: string) =>
          isNotUndefined(BRAND_VALUES(strictLocalize)[value])
        )
        .map((value: string) => BRAND_VALUES(strictLocalize)[value]?.label);
      return readableMakerValues.join(", ");
    }

    if (showEuropeFreeShippingSubheader) {
      return strictLocalize({
        defaultMessage: "Shop all European brands with free shipping",
      });
    }

    if (mostSpecificPathIdentifier === undefined) {
      return undefined;
    }

    return subheaderForLoggedInUsers(strictLocalize)[
      mostSpecificPathIdentifier
    ];
  }, [
    layoutElementSubheader,
    isHubCategory,
    isHubC1Subcategory,
    mostSpecificPathIdentifier,
    isNewProductsC1,
    isNewBrandsC1,
    isPrimaryCategory,
    urlId.id,
    isNewProductsSubCategory,
    isNewArrivalsSubCategory,
    categoryModel,
    selectedMakerValues,
    showEuropeFreeShippingSubheader,
    strictLocalize,
    isNonAmericanInsider,
    helpCenterLink,
    mostSpecificName,
  ]);

  useEffectToTrackEuropeFreeShippingSubheaderMilestone(
    showEuropeFreeShippingSubheader,
    subheaderText
  );

  return { headerText, subheaderText, gridHeaderText };
};

const useSelectedMakerValues = () => {
  const filterKeys = useCategoryPageFilterKeys();
  return useMemo(() => {
    return filterKeys
      .filter((filterKey: string) =>
        isFilterKeyOfType(filterKey, BRAND_VALUE_FILTER_TYPE)
      )
      .map((filterKey) => getFilterValueFromFilterKey(filterKey))
      .filter(isNotUndefined);
  }, [filterKeys]);
};

const useEffectToTrackEuropeFreeShippingSubheaderMilestone = (
  showEuropeFreeShippingSubheader: boolean,
  subheaderText?: string | JSX.Element
) => {
  const recordMilestoneOnce = useRecordMilestoneOnce();

  // set milestone when EU free shipping subheader is shown
  useEffect(() => {
    if (showEuropeFreeShippingSubheader) {
      recordMilestoneOnce(
        "RETAILER_BUCKETED_INTO_UK_EU_BRANDS_FREE_SHIPPING_EXPERIMENT"
      );
    }
  }, [recordMilestoneOnce, showEuropeFreeShippingSubheader, subheaderText]);
};

const getParentCategoryName = (category: Category): string | undefined => {
  if (category.subCategory?.subCategory !== undefined) {
    return getParentCategoryName(category.subCategory);
  }

  return category.name;
};
